// Transformer to use with the TRPC API
import SuperJSON from 'superjson';
import { DateTime } from 'luxon';
import { z } from 'zod';
import { type ObjectId, isObjectId, Branded } from '@repo/common/interfaces';

const InvalidDateTimeDTO = z.object({
  isInvalid: z.literal(true),
  reason: z.string(),
  explanation: z.string().optional(),
});
type InvalidDateTimeDTO = z.infer<typeof InvalidDateTimeDTO>;

SuperJSON.registerCustom<DateTime, string | InvalidDateTimeDTO>(
  {
    isApplicable: (v): v is DateTime => DateTime.isDateTime(v),
    serialize: (v) => {
      if (v.isValid) {
        return (v as DateTime<true>).toISO();
      } else {
        const { invalidReason: reason, invalidExplanation: explanation } = v as DateTime<false>;
        return { isInvalid: true, reason, explanation: explanation || undefined };
      }
    },
    deserialize: (v) => {
      if (typeof v === 'string') {
        return DateTime.fromISO(v);
      } else {
        const { reason, explanation } = v;
        return DateTime.invalid(reason, explanation);
      }
    },
  },
  'DateTime'
);

// Special branded type to prevent TypeScript from being overzealous and thinking this transformer applies to all strings
type ObjectIdString = Branded<string, 'ObjectIdString'>;
const objectIdRegex = /^[0-9a-fA-F]{24}$/;
const isObjectIdString = (v: unknown): v is ObjectIdString =>
  typeof v === 'string' && objectIdRegex.test(v);

// One-way serializer to convert ObjectIds into strings
SuperJSON.registerCustom<ObjectId | ObjectIdString, string>(
  {
    isApplicable: (v: unknown) => isObjectId(v) || isObjectIdString(v),
    serialize: (v) => v.toString(),
    deserialize: (v) => v as ObjectIdString,
  },
  'ObjectIdString'
);

// TODO: specify typing somehow for serialized mongoose documents

export const transformer = SuperJSON;
